/**
 * Transaction Board — TC (Transaction Coordination) Kanban.
 *
 * Every column is a stage between "contract signed" and "assignment paid".
 * Cards are DocuSign envelopes whose status is completed/signed.
 * Click a card to hop to the Deal Card. Click "Advance →" to move a card
 * to the next lane. TC notes edit inline.
 *
 * Backend surface:
 *   GET   /api/v3/transactions
 *   PATCH /api/v3/transactions/:id/stage    { stage }
 *   PATCH /api/v3/transactions/:id/note     { note }
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ROLE } from "@/lib/palette";
import AssignmentPaidCelebration from "@/components/v3/AssignmentPaidCelebration";
import {
  ArrowRight, ChevronRight, DollarSign, FileSignature, Building2,
  User, Clock, StickyNote, TrendingUp, Loader2, CheckCircle2,
  Landmark, ShieldCheck, X as XIcon,
} from "lucide-react";

const fmtMoney = (n) => {
  const num = Number(n) || 0;
  if (!num) return "—";
  return num >= 1000
    ? `$${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}k`
    : `$${num.toFixed(0)}`;
};
const fmtDays = (d) => (d == null ? "—" : `${d}d`);

// ── The board ───────────────────────────────────────────────────────
export default function TransactionsBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [paidCard, setPaidCard] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v3/transactions");
      setData(data);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const advance = useCallback(async (card, toStage) => {
    setSaving((s) => ({ ...s, [card.id]: true }));
    try {
      await api.patch(`/v3/transactions/${card.id}/stage`, { stage: toStage });
      toast.success(`Moved to ${toStage.replace(/_/g, " ")}`);
      // Confetti celebration when the deal actually LANDS on assignment_paid.
      if (toStage === "assignment_paid" && card.stage !== "assignment_paid") {
        setPaidCard({ ...card, stage: toStage });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to advance stage");
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[card.id]; return n; });
    }
  }, [load]);

  const saveNote = useCallback(async (card, note) => {
    try {
      await api.patch(`/v3/transactions/${card.id}/note`, { note });
      toast.success("Note saved");
    } catch {
      toast.error("Failed to save note");
    }
  }, []);

  const saveEmd = useCallback(async (card, patch) => {
    try {
      const res = await api.patch(`/v3/transactions/${card.id}/emd`, patch);
      if (res.data?.auto_advanced_to) {
        toast.success(`EMD received — auto-advanced to ${res.data.auto_advanced_to.replace(/_/g, " ")}`);
      } else {
        toast.success("EMD updated");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save EMD");
    }
  }, [load]);

  const savePof = useCallback(async (card, patch) => {
    try {
      const res = await api.patch(`/v3/transactions/${card.id}/pof`, patch);
      if (res.data?.auto_advanced_to) {
        toast.success(`POF verified — auto-advanced to ${res.data.auto_advanced_to.replace(/_/g, " ")}`);
      } else {
        toast.success("POF updated");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save POF");
    }
  }, [load]);

  const stages = data?.stages || [];
  const lanes  = data?.lanes  || [];
  const totals = data?.totals || {};

  const emptyBoard = !loading && lanes.every((l) => (l.count || 0) === 0);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="transactions-board">
      <AssignmentPaidCelebration card={paidCard} onClose={() => setPaidCard(null)} />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-indigo-600 mb-1">
            Transaction Coordination
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-slate-900">
            Transaction Board
          </h1>
          <p className="text-slate-500 text-sm mt-1.5 max-w-2xl">
            Every signed PA flows through these lanes until the assignment fee lands.
            One screen, no lost deals. Click <span className="font-semibold">Advance →</span> to march a card forward.
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3 min-w-[420px]" data-testid="tc-totals">
          <MiniStat label="In-flight" value={totals.in_flight_count ?? 0} role="cold" testid="tc-total-inflight" icon={FileSignature} />
          <MiniStat label="Pipeline $" value={fmtMoney(totals.in_flight_value)} role="hot" testid="tc-total-value" icon={DollarSign} />
          <MiniStat label="Funded" value={totals.funded_count ?? 0} role="won" testid="tc-total-funded" icon={TrendingUp} />
        </div>
      </div>

      {/* Board */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white h-96 grid place-items-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : emptyBoard ? (
        <EmptyBoard />
      ) : (
        <div className="overflow-x-auto pb-4" data-testid="tc-lanes-scroll">
          <div className="flex gap-4 min-w-max">
            {lanes.map((lane, idx) => {
              const nextStage = stages[idx + 1]?.key;
              return (
                <Lane
                  key={lane.key}
                  lane={lane}
                  nextStage={nextStage}
                  saving={saving}
                  onAdvance={advance}
                  onSaveNote={saveNote}
                  onSaveEmd={saveEmd}
                  onSavePof={savePof}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mini stat tile (top-right of the header) ─────────────────────────
function MiniStat({ label, value, role, testid, icon: Icon }) {
  const r = ROLE[role] || ROLE.info;
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 flex items-center gap-2.5"
      data-testid={testid}
    >
      <span
        className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
        style={{ backgroundColor: r.bg, color: r.fg }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 leading-none">
          {label}
        </div>
        <div className="text-xl font-black tabular-nums text-slate-900 leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
}

// ── A single lane column ─────────────────────────────────────────────
function Lane({ lane, nextStage, saving, onAdvance, onSaveNote, onSaveEmd, onSavePof }) {
  const r = ROLE[lane.role] || ROLE.info;
  return (
    <div
      className="w-[300px] shrink-0 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col"
      data-testid={`tc-lane-${lane.key}`}
    >
      {/* Lane header */}
      <div
        className="px-3.5 py-3 rounded-t-2xl border-b border-slate-200 flex items-center gap-2"
        style={{ backgroundColor: r.chipBg }}
      >
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: r.dot }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.14em]" style={{ color: r.chipFg }}>
            {lane.label}
          </div>
          <div className="text-[10.5px] text-slate-500 tabular-nums" data-testid={`tc-lane-${lane.key}-meta`}>
            {lane.count} card{lane.count === 1 ? "" : "s"}
            {lane.pipeline_value > 0 ? ` · ${fmtMoney(lane.pipeline_value)}` : ""}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="p-2.5 flex-1 space-y-2.5 min-h-[120px]" data-testid={`tc-lane-${lane.key}-cards`}>
        {lane.cards.length === 0 ? (
          <div className="text-[11.5px] text-slate-400 text-center py-8 italic">
            No cards
          </div>
        ) : (
          lane.cards.map((card) => (
            <TxCard
              key={card.id}
              card={card}
              nextStage={nextStage}
              saving={!!saving[card.id]}
              onAdvance={onAdvance}
              onSaveNote={onSaveNote}
              onSaveEmd={onSaveEmd}
              onSavePof={onSavePof}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── A single transaction card ────────────────────────────────────────
function TxCard({ card, nextStage, saving, onAdvance, onSaveNote, onSaveEmd, onSavePof }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(card.note || "");
  const [editor, setEditor] = useState(null); // "emd" | "pof" | null
  const propertyHref = card.property_id ? `/v3/deal/${card.property_id}` : null;

  return (
    <div
      className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all"
      data-testid={`tc-card-${card.id}`}
    >
      {/* Property */}
      <div className="flex items-start gap-2 mb-2">
        <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
        {propertyHref ? (
          <Link
            to={propertyHref}
            className="text-[12.5px] font-semibold text-slate-900 hover:text-indigo-600 line-clamp-2 leading-tight"
            data-testid={`tc-card-${card.id}-property`}
          >
            {card.property_address}
          </Link>
        ) : (
          <span className="text-[12.5px] font-semibold text-slate-900 line-clamp-2 leading-tight">
            {card.property_address}
          </span>
        )}
      </div>

      {/* Buyer */}
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-600 mb-1.5">
        <User className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="truncate">{card.buyer_name}</span>
      </div>

      {/* Meta row */}
      <div className="flex items-center justify-between text-[11px] text-slate-500 tabular-nums mb-2">
        <span className="inline-flex items-center gap-1">
          <DollarSign className="w-3 h-3" /> {fmtMoney(card.assignment_fee)}
        </span>
        <span className="inline-flex items-center gap-1" title="Days in this stage">
          <Clock className="w-3 h-3" /> {fmtDays(card.days_in_stage)}
        </span>
      </div>

      {/* EMD + POF chips */}
      <div className="grid grid-cols-2 gap-1.5 mb-2.5" data-testid={`tc-card-${card.id}-emd-pof-row`}>
        <ChipButton
          testid={`tc-card-${card.id}-emd-chip`}
          icon={Landmark}
          label="EMD"
          amount={card.emd_amount}
          done={card.emd_received}
          onClick={() => setEditor(editor === "emd" ? null : "emd")}
        />
        <ChipButton
          testid={`tc-card-${card.id}-pof-chip`}
          icon={ShieldCheck}
          label="POF"
          amount={card.pof_amount}
          done={card.pof_verified}
          onClick={() => setEditor(editor === "pof" ? null : "pof")}
        />
      </div>

      {editor === "emd" && (
        <EmdEditor
          card={card}
          onCancel={() => setEditor(null)}
          onSave={async (patch) => { await onSaveEmd(card, patch); setEditor(null); }}
        />
      )}
      {editor === "pof" && (
        <PofEditor
          card={card}
          onCancel={() => setEditor(null)}
          onSave={async (patch) => { await onSavePof(card, patch); setEditor(null); }}
        />
      )}

      {/* Note editor */}
      {noteOpen ? (
        <div className="mb-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full text-[11.5px] border border-slate-200 rounded-md p-1.5 focus:outline-none focus:border-indigo-400 resize-none"
            placeholder="TC note (title co., escrow ref#, blocker…)"
            data-testid={`tc-card-${card.id}-note-input`}
          />
          <div className="flex justify-end gap-1.5 mt-1">
            <button
              type="button"
              onClick={() => { setNote(card.note || ""); setNoteOpen(false); }}
              className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1"
              data-testid={`tc-card-${card.id}-note-cancel`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => { await onSaveNote(card, note); setNoteOpen(false); }}
              className="text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2 py-1"
              data-testid={`tc-card-${card.id}-note-save`}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setNoteOpen(true)}
          className="text-[11px] text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1 mb-2 w-full text-left"
          data-testid={`tc-card-${card.id}-note-toggle`}
        >
          <StickyNote className="w-3 h-3 shrink-0" />
          <span className="truncate">{card.note ? card.note : "Add note"}</span>
        </button>
      )}

      {/* Advance CTA */}
      {nextStage ? (
        <button
          type="button"
          disabled={saving}
          onClick={() => onAdvance(card, nextStage)}
          className="w-full inline-flex items-center justify-center gap-1 text-[11.5px] font-bold text-white rounded-lg py-1.5 disabled:opacity-50 transition-all"
          style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}
          data-testid={`tc-card-${card.id}-advance`}
          title={`Advance to ${nextStage.replace(/_/g, " ")}`}
        >
          {saving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>Advance <ArrowRight className="w-3 h-3" /></>
          )}
        </button>
      ) : (
        <div
          className="w-full inline-flex items-center justify-center gap-1 text-[11.5px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-1.5"
          data-testid={`tc-card-${card.id}-done`}
        >
          <CheckCircle2 className="w-3 h-3" /> Complete
        </div>
      )}
    </div>
  );
}


// ── EMD/POF chip button ────────────────────────────────────────────────
function ChipButton({ testid, icon: Icon, label, amount, done, onClick }) {
  const money = Number(amount) || 0;
  const hasAmount = money > 0;
  const cls = done
    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : hasAmount
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center justify-between gap-1 px-2 py-1.5 rounded-lg border text-[10.5px] font-bold transition-all ${cls}`}
      title={done ? `${label} confirmed` : hasAmount ? `${label} pending confirmation` : `Track ${label}`}
    >
      <span className="inline-flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </span>
      <span className="tabular-nums">
        {done ? <CheckCircle2 className="w-3 h-3" /> : (hasAmount ? fmtMoney(money) : "—")}
      </span>
    </button>
  );
}


// ── Inline EMD editor ─────────────────────────────────────────────────
function EmdEditor({ card, onSave, onCancel }) {
  const [amount, setAmount] = useState(String(card.emd_amount ?? ""));
  const [ref, setRef]       = useState(card.emd_escrow_ref || "");
  const [received, setReceived] = useState(!!card.emd_received);
  return (
    <div
      className="mb-2 rounded-lg border border-amber-200 bg-amber-50/50 p-2 space-y-1.5"
      data-testid={`tc-card-${card.id}-emd-editor`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] font-black uppercase tracking-widest text-amber-800 inline-flex items-center gap-1">
          <Landmark className="w-3 h-3" /> Earnest Money
        </div>
        <button
          type="button" onClick={onCancel}
          className="text-slate-400 hover:text-slate-600"
          data-testid={`tc-card-${card.id}-emd-close`}
        >
          <XIcon className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <input
          type="number" inputMode="decimal" placeholder="EMD $"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          className="text-[11.5px] border border-amber-200 bg-white rounded px-1.5 py-1 focus:outline-none focus:border-amber-400"
          data-testid={`tc-card-${card.id}-emd-amount`}
        />
        <input
          type="text" placeholder="Escrow ref#"
          value={ref} onChange={(e) => setRef(e.target.value)}
          className="text-[11.5px] border border-amber-200 bg-white rounded px-1.5 py-1 focus:outline-none focus:border-amber-400"
          data-testid={`tc-card-${card.id}-emd-ref`}
        />
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
        <input
          type="checkbox" checked={received}
          onChange={(e) => setReceived(e.target.checked)}
          data-testid={`tc-card-${card.id}-emd-received`}
        />
        Received / cleared escrow
      </label>
      <div className="flex justify-end gap-1.5">
        <button
          type="button" onClick={onCancel}
          className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1"
        >Cancel</button>
        <button
          type="button"
          onClick={() => onSave({
            amount: amount === "" ? null : Number(amount),
            escrow_ref: ref,
            received,
          })}
          className="text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 rounded px-2 py-1"
          data-testid={`tc-card-${card.id}-emd-save`}
        >Save</button>
      </div>
    </div>
  );
}


// ── Inline POF editor ─────────────────────────────────────────────────
function PofEditor({ card, onSave, onCancel }) {
  const [amount,   setAmount]   = useState(String(card.pof_amount ?? ""));
  const [source,   setSource]   = useState(card.pof_source || "");
  const [docUrl,   setDocUrl]   = useState(card.pof_doc_url || "");
  const [verified, setVerified] = useState(!!card.pof_verified);
  return (
    <div
      className="mb-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2 space-y-1.5"
      data-testid={`tc-card-${card.id}-pof-editor`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] font-black uppercase tracking-widest text-indigo-800 inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Proof of Funds
        </div>
        <button
          type="button" onClick={onCancel}
          className="text-slate-400 hover:text-slate-600"
          data-testid={`tc-card-${card.id}-pof-close`}
        >
          <XIcon className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <input
          type="number" inputMode="decimal" placeholder="POF $"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          className="text-[11.5px] border border-indigo-200 bg-white rounded px-1.5 py-1 focus:outline-none focus:border-indigo-400"
          data-testid={`tc-card-${card.id}-pof-amount`}
        />
        <input
          type="text" placeholder="Source (bank / funder)"
          value={source} onChange={(e) => setSource(e.target.value)}
          className="text-[11.5px] border border-indigo-200 bg-white rounded px-1.5 py-1 focus:outline-none focus:border-indigo-400"
          data-testid={`tc-card-${card.id}-pof-source`}
        />
      </div>
      <input
        type="url" placeholder="Link to PDF / bank letter (optional)"
        value={docUrl} onChange={(e) => setDocUrl(e.target.value)}
        className="w-full text-[11.5px] border border-indigo-200 bg-white rounded px-1.5 py-1 focus:outline-none focus:border-indigo-400"
        data-testid={`tc-card-${card.id}-pof-doc-url`}
      />
      <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
        <input
          type="checkbox" checked={verified}
          onChange={(e) => setVerified(e.target.checked)}
          data-testid={`tc-card-${card.id}-pof-verified`}
        />
        Verified (bank letter / statement seen)
      </label>
      <div className="flex justify-end gap-1.5">
        <button
          type="button" onClick={onCancel}
          className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1"
        >Cancel</button>
        <button
          type="button"
          onClick={() => onSave({
            amount: amount === "" ? null : Number(amount),
            source, doc_url: docUrl, verified,
          })}
          className="text-[11px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2 py-1"
          data-testid={`tc-card-${card.id}-pof-save`}
        >Save</button>
      </div>
    </div>
  );
}

// ── Empty-state pane ─────────────────────────────────────────────────
function EmptyBoard() {
  return (
    <div
      className="rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 py-16 px-8 text-center"
      data-testid="tc-empty-state"
    >
      <FileSignature className="w-10 h-10 text-indigo-500 mx-auto mb-4" />
      <div className="text-lg font-black text-slate-900 mb-1">
        No signed contracts yet
      </div>
      <p className="text-slate-500 text-sm max-w-md mx-auto mb-5 leading-relaxed">
        The Transaction Board fills up automatically the moment your DocuSign
        envelopes are signed. Send a Purchase Agreement and it lands in
        <span className="font-semibold text-indigo-700"> Contract Signed</span> on this
        board.
      </p>
      <Link
        to="/v3/crm/contracts"
        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2.5"
        data-testid="tc-empty-cta"
      >
        Go to Contracts <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
